
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {DashboardInfoRenameResponse} from './DashboardInfoRenameResponse';

/**
 * DashboardInfoRenameRequest struct
 */
class DashboardInfoRenameRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!UUID=} dashboardId
   * @param {!number=} version
   * @param {string=} newDashboardName
   * @constructor
   */
  constructor (parent = new Request(), dashboardId = new UUID(), version = new UInt64(0, 0), newDashboardName = null) {
    super()
    super.copy(parent)
    this.DashboardId = dashboardId
    this.Version = version
    this.NewDashboardName = newDashboardName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DashboardInfoRenameRequest}
   * @param {!DashboardInfoRenameRequest} other Other struct
   * @returns {!DashboardInfoRenameRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.DashboardId != null) {
      this.DashboardId = new UUID(other.DashboardId)
    } else {
      this.DashboardId = null
    }
    if (other.Version != null) {
      this.Version = UInt64.fromNumber(other.Version)
    } else {
      this.Version = null
    }
    if (other.NewDashboardName != null) {
      this.NewDashboardName = other.NewDashboardName
    } else {
      this.NewDashboardName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DashboardInfoRenameRequest}
   * @returns {!DashboardInfoRenameRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DashboardInfoRenameRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DashboardInfoRenameRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DashboardInfoRenameRequest}
   * @param {!DashboardInfoRenameRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DashboardInfoRenameRequest)) {
      throw new TypeError('Instance of DashboardInfoRenameRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DashboardInfoRenameRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      DashboardId: ((this.DashboardId != null) ? this.DashboardId.toString() : null),
      Version: ((this.Version != null) ? this.Version.toNumber() : null),
      NewDashboardName: ((this.NewDashboardName != null) ? this.NewDashboardName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DashboardInfoRenameRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DashboardInfoRenameRequest} other Object value
   * @returns {!DashboardInfoRenameRequest} Created struct
   */
  static fromObject (other) {
    return new DashboardInfoRenameRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DashboardInfoRenameRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DashboardInfoRenameRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DashboardInfoRenameRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5185
  }
}

export { DashboardInfoRenameRequest };

/**
 * Fast Binary Encoding DashboardInfoRenameRequest field model
 */
class FieldModelDashboardInfoRenameRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.DashboardId = new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Version = new fbe.FieldModelUInt64(buffer, this.DashboardId.fbeOffset + this.DashboardId.fbeSize)
    this.NewDashboardName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.Version.fbeOffset + this.Version.fbeSize), buffer, this.Version.fbeOffset + this.Version.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.DashboardId.fbeSize + this.Version.fbeSize + this.NewDashboardName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.DashboardId.fbeExtra + this.Version.fbeExtra + this.NewDashboardName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDashboardInfoRenameRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5185
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DashboardId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    if ((fbeCurrentSize + this.NewDashboardName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDashboardName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDashboardName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!DashboardInfoRenameRequest} fbeValue Default value, defaults is new DashboardInfoRenameRequest()
   * @returns {!DashboardInfoRenameRequest} DashboardInfoRenameRequest value
   */
  get (fbeValue = new DashboardInfoRenameRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!DashboardInfoRenameRequest} fbeValue DashboardInfoRenameRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) <= fbeStructSize) {
      fbeValue.DashboardId = this.DashboardId.get()
    } else {
      fbeValue.DashboardId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) <= fbeStructSize) {
      fbeValue.Version = this.Version.get()
    } else {
      fbeValue.Version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    if ((fbeCurrentSize + this.NewDashboardName.fbeSize) <= fbeStructSize) {
      fbeValue.NewDashboardName = this.NewDashboardName.get()
    } else {
      fbeValue.NewDashboardName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDashboardName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {!DashboardInfoRenameRequest} fbeValue DashboardInfoRenameRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDashboardInfoRenameRequest}
   * @param {DashboardInfoRenameRequest} fbeValue DashboardInfoRenameRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.DashboardId.set(fbeValue.DashboardId)
    this.Version.set(fbeValue.Version)
    this.NewDashboardName.set(fbeValue.NewDashboardName)
  }
}

export { FieldModelDashboardInfoRenameRequest };

/**
 * Fast Binary Encoding DashboardInfoRenameRequest model
 */
class DashboardInfoRenameRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDashboardInfoRenameRequest(this.buffer, 4)
  }

  /**
   * Get the DashboardInfoRenameRequest model
   * @this {!DashboardInfoRenameRequestModel}
   * @returns {!FieldModelDashboardInfoRenameRequest} model DashboardInfoRenameRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DashboardInfoRenameRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DashboardInfoRenameRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DashboardInfoRenameRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DashboardInfoRenameRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDashboardInfoRenameRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DashboardInfoRenameRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DashboardInfoRenameRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DashboardInfoRenameRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DashboardInfoRenameRequestModel}
   * @param {!DashboardInfoRenameRequest} value DashboardInfoRenameRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DashboardInfoRenameRequestModel}
   * @param {!DashboardInfoRenameRequest} value DashboardInfoRenameRequest value, defaults is new DashboardInfoRenameRequest()
   * @return {!object} Deserialized DashboardInfoRenameRequest value and its size
   */
  deserialize (value = new DashboardInfoRenameRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DashboardInfoRenameRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DashboardInfoRenameRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DashboardInfoRenameRequestModel}
   * @param {!number} prev Previous DashboardInfoRenameRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DashboardInfoRenameRequestModel };
DashboardInfoRenameRequest.__has_response = true;
DashboardInfoRenameRequest.__response_class = DashboardInfoRenameResponse;
